/**
 * atomberg-magic-checkout-analytics.js
 * ─────────────────────────────────────
 * Must load synchronously, BEFORE magic-shopify.js, so it can intercept
 * window.Razorpay before Razorpay's checkout SDK assigns the real constructor
 * to it (see razorpay-magic-checkout.liquid for load order).
 *
 * Razorpay Magic Checkout creates its own Razorpay instance internally, inside
 * magic-shopify.js's closure, and never exposes it — confirmed with Razorpay
 * support that there is no documented hook to attach an 'mx-analytics'
 * listener for this Shopify plugin integration (their suggested
 * `new Razorpay(...)` pattern assumes a merchant-managed checkout, which this
 * plugin is not). The merchant-dashboard GA4 integration was also ruled out
 * with the Razorpay team directly — its custom event/field mapping isn't
 * configurable enough to match the exact schema GTM/Meta are already trained
 * on from the Magento migration.
 *
 * Two independent signals are bridged onto `window` as CustomEvents,
 * namespaced `magic:*` to match this theme's existing shopify:section:load
 * convention. This file has no GTM/dataLayer schema knowledge — it only
 * bridges signals; atomberg-gtm-datalayer.js owns building the exact event
 * names and payload keys GTM/Meta are trained on from each payload.
 *
 * 1. magic:<mx-analytics event name> (initiate, shipping_selected,
 *    payment_initiated, coupon_applied, otp_submitted, etc. — verbatim,
 *    Razorpay's own names). Captured via a Proxy `construct` trap on
 *    window.Razorpay: any `new window.Razorpay(...)` call — including the one
 *    inside magic-shopify.js — passes through the proxy, handing us the real
 *    instance to call `.on('mx-analytics', ...)` on.
 *
 *    A Proxy, not a manually rebuilt wrapper function, is deliberate: an
 *    earlier version of this file rebuilt window.Razorpay as a plain function
 *    and copied the real constructor's static properties onto it by hand.
 *    That copy used `for...in` (enumerable-only) and broke live checkout,
 *    because real SDKs commonly define static members as non-enumerable —
 *    magic-shopify.js called one of those missing methods and threw. A Proxy
 *    forwards every operation (property reads, instanceof, the prototype
 *    chain — enumerable or not) to the real constructor by default; we only
 *    override the one trap we actually need (`construct`). This eliminates
 *    that whole class of bug rather than patching around it.
 *
 * 2. magic:complete (order completion). Razorpay's own checkout-completion
 *    code calls `window.onCompleteMagiCheckout(order_id, total_amount, order)`
 *    if that global function exists — a plain, optional hook they check for,
 *    requiring no wrapping or interception at all. `order` carries the full
 *    order object (customer_details, shipping_fee, total_tax, promotions,
 *    cart/line-item data) — richer and more reliable than anything the
 *    Proxy/mx-analytics path can give us for purchase, so this is the sole
 *    purchase signal; the Proxy path does not attempt to cover purchase.
 *
 * FAIL-OPEN BY DESIGN — this is a live payment funnel, so nothing here may
 * ever prevent or corrupt the real checkout instance from being constructed,
 * or disrupt Razorpay's own order-completion flow:
 *   - the Proxy only traps `construct`; every other operation (property
 *     reads, instanceof, prototype chain) passes through to the real
 *     constructor automatically — nothing is manually rebuilt or copied
 *   - the real construction itself (Reflect.construct) is never wrapped in
 *     try/catch — its errors, if checkout itself is broken, must propagate
 *   - our own instrumentation (mx-analytics bind, onCompleteMagiCheckout
 *     body) is individually try/caught; a failure there is swallowed
 *     silently and we simply lose that piece of tracking
 *   - if creating the Proxy fails for any reason, window.Razorpay falls back
 *     to the raw, unwrapped constructor so checkout is unaffected
 *   - onCompleteMagiCheckout is called inline inside Razorpay's own
 *     completion handler with no try/catch on their side, so our
 *     implementation must never throw — everything in it is wrapped
 *
 * Fragile by design in what it CAN capture: it relies on Razorpay's current
 * load order (a placeholder object assigned first, replaced later by the real
 * constructor function) and on onCompleteMagiCheckout continuing to be called
 * the way current decompiled behavior suggests. If Razorpay changes either,
 * this no-ops — Magic Checkout keeps working normally, we just stop receiving
 * these events.
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w._atombergRzpInterceptorInit) return;
  w._atombergRzpInterceptorInit = true;

  /** @type {any} */
  var current = w.Razorpay;

  /**
   * Bridge Magic Checkout's mx-analytics events onto window as CustomEvents,
   * under Razorpay's own event names verbatim (initiate, shipping_selected,
   * payment_initiated, coupon_applied, otp_submitted, ...) — no renaming here;
   * atomberg-gtm-datalayer.js maps each to the trained GTM event/payload.
   * @param {any} instance
   */
  function forwardMxAnalytics(instance) {
    if (!instance || typeof instance.on !== 'function') return;
    instance.on('mx-analytics', function (/** @type {any} */ data) {
      if (!data || !data.event) return;
      w.dispatchEvent(new CustomEvent('magic:' + data.event, { detail: data }));
    });
  }

  try {
    if (typeof current === 'function') {
      // real constructor is already in place at load time — proxy it now
      current = new Proxy(current, {
        construct: function (target, args, newTarget) {
          var instance = Reflect.construct(target, args, newTarget);
          try {
            forwardMxAnalytics(instance);
          } catch (_) {}
          return instance;
        },
      });
    }
  } catch (_) {
    // leave current as whatever it already was — never block checkout
  }

  Object.defineProperty(w, 'Razorpay', {
    configurable: true,
    get: function () {
      return current;
    },
    set: function (/** @type {any} */ value) {
      // guard against magic-shopify.js's own `window.Razorpay || (window.Razorpay = {})`
      // defensive re-assignment re-wrapping an already-wrapped constructor
      if (value === current) return;

      if (typeof value !== 'function') {
        // still the placeholder-object phase (static config writes land here)
        current = value;
        return;
      }

      try {
        current = new Proxy(value, {
          construct: function (target, args, newTarget) {
            var instance = Reflect.construct(target, args, newTarget);
            try {
              forwardMxAnalytics(instance);
            } catch (_) {
              // analytics bridging must never affect the returned instance
            }
            return instance;
          },
        });
      } catch (_) {
        // Proxy creation failed for some reason we didn't anticipate — fall
        // back to the real, unwrapped constructor so checkout is never
        // blocked by us
        current = value;
      }
    },
  });

  // ── Purchase — window.onCompleteMagiCheckout ──────────────────────────────
  // Must never throw: Razorpay calls this inline, uncaught, inside their own
  // async order-completion handler — an exception here would propagate into
  // their flow and could break cart-clear/redirect logic that runs after it.
  w.onCompleteMagiCheckout = function (
    /** @type {any} */ orderId,
    /** @type {any} */ totalAmount,
    /** @type {any} */ order
  ) {
    try {
      w.dispatchEvent(
        new CustomEvent('magic:complete', {
          detail: { orderId: orderId, totalAmount: totalAmount, order: order || {} },
        })
      );
    } catch (_) {}
  };
})();
